import { useState } from "react";
import type { LocationInput as Location, Member } from "../api/types";
import { geocodeAddress } from "../googleMaps";
import { LocationInput } from "./LocationInput";

interface Props {
  members: Member[];
  maxGroupSize: number;
  busy: boolean;
  onAdd: (name: string, location: Location) => Promise<void>;
  onRemove: (memberId: string) => Promise<void>;
  apiKey: string | null;
}

interface SlotState {
  name: string;
  where: string;
  resolved: Location | null;
  locating: boolean;
}

const EMPTY_SLOT: SlotState = {
  name: "",
  where: "",
  resolved: null,
  locating: false,
};

export function PeopleStep({
  members,
  maxGroupSize,
  busy,
  onAdd,
  onRemove,
  apiKey,
}: Props) {
  // Start with 2 empty slots when no members yet, 1 slot otherwise.
  const defaultCount = members.length === 0 ? 2 : 1;
  const [slots, setSlots] = useState<SlotState[]>(
    Array.from({ length: defaultCount }, () => ({ ...EMPTY_SLOT }))
  );
  const full = members.length >= maxGroupSize;
  const canAddSlot =
    members.length + slots.length < maxGroupSize && slots.length < 5;

  function updateSlot(
    index: number,
    patch: Partial<SlotState>,
  ) {
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s))
    );
  }

  function useMyLocation(index: number) {
    if (!navigator.geolocation) return;
    updateSlot(index, { locating: true });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        updateSlot(index, {
          resolved: { point: { lat: latitude, lng: longitude } },
          where: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          locating: false,
        });
        reverseGeocode(latitude, longitude).then((label) => {
          if (label) updateSlot(index, { where: label });
        });
      },
      () => updateSlot(index, { locating: false }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function submitAll(event: React.FormEvent) {
    event.preventDefault();
    // Collect all filled slots, then submit them sequentially and
    // clear them in one batch. Submitting one-at-a-time and removing
    // mid-loop would shift indices and lose the second slot.
    const filled = slots
      .map((slot, i) => ({ slot, i }))
      .filter(({ slot }) => slot.where.trim());
    if (filled.length === 0 || full) return;

    let addedCount = 0;
    for (const { slot } of filled) {
      const finalName =
        slot.name.trim() || `Person ${members.length + addedCount + 1}`;

      let location: Location =
        slot.resolved ?? { area: slot.where.trim() };

      if (!slot.resolved) {
        const coords = await geocodeAddress(
          slot.where.trim(),
          members[0]?.point,
        );
        if (coords) location = { point: coords };
      }

      await onAdd(finalName, location);
      addedCount++;
    }

    // Remove all submitted slots, keep unfilled ones
    setSlots((prev) => {
      const remaining = prev.filter((s) => !s.where.trim());
      return remaining.length > 0
        ? remaining
        : [{ ...EMPTY_SLOT }];
    });
  }

  function addSlot() {
    if (canAddSlot) {
      setSlots((prev) => [...prev, { ...EMPTY_SLOT }]);
    }
  }

  function removeSlot(index: number) {
    setSlots((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }

  const geoAvailable =
    typeof navigator !== "undefined" && "geolocation" in navigator;
  // How many slots have a location filled in
  const filledCount = slots.filter((s) => s.where.trim()).length;

  return (
    <section className="card">
      <h2>Who's coming?</h2>
      <p className="muted">
        A neighbourhood is enough — "Indiranagar" works as well as
        a full address.
      </p>

      {members.length > 0 && (
        <ul className="people">
          {members.map((member) => (
            <li key={member.member_id}>
              <span className="person-name">{member.name}</span>
              <span className="muted">{member.resolved_label}</span>
              {member.precision === "area" && (
                <span
                  className="chip"
                  title="Area-level input, sampled across the neighbourhood"
                >
                  approx
                </span>
              )}
              <button
                type="button"
                className="link"
                onClick={() => void onRemove(member.member_id)}
                disabled={busy}
                aria-label={`Remove ${member.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {!full && (
        <form onSubmit={submitAll}>
          {slots.map((slot, index) => (
            <div className="row person-row" key={index}>
              <input
                value={slot.name}
                onChange={(e) =>
                  updateSlot(index, { name: e.target.value })
                }
                placeholder={`Person ${members.length + index + 1}`}
                aria-label="Name"
                disabled={busy}
                maxLength={40}
              />
              <div className="location-group">
                <LocationInput
                  value={slot.where}
                  onChange={(text) =>
                    updateSlot(index, {
                      where: text,
                      resolved: null,
                    })
                  }
                  onResolve={(loc) =>
                    updateSlot(index, { resolved: loc })
                  }
                  disabled={busy || slot.locating}
                  placeholder="Where are they coming from?"
                  near={members[0]?.point}
                  apiKey={apiKey}
                />
                {geoAvailable && (
                  <button
                    type="button"
                    className="locate-btn"
                    onClick={() => useMyLocation(index)}
                    disabled={busy || slot.locating}
                    title="Use my current location"
                    aria-label="Use my current location"
                  >
                    {slot.locating ? "..." : "\u2316"}
                  </button>
                )}
              </div>
              {slots.length > 1 && (
                <button
                  type="button"
                  className="link slot-remove"
                  onClick={() => removeSlot(index)}
                  disabled={busy}
                  aria-label="Remove this row"
                >
                  &times;
                </button>
              )}
            </div>
          ))}

          <div className="person-actions">
            {canAddSlot && (
              <button
                type="button"
                className="link"
                onClick={addSlot}
                disabled={busy}
              >
                + Add another person
              </button>
            )}
            <button
              type="submit"
              className="primary"
              disabled={busy || filledCount === 0}
            >
              {busy
                ? "Adding..."
                : filledCount > 1
                  ? `Add ${filledCount} people`
                  : "Add"}
            </button>
          </div>
        </form>
      )}

      {full && (
        <p className="muted small">
          That's the {maxGroupSize}-person limit for now.
        </p>
      )}
    </section>
  );
}

async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<string | null> {
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=16`,
      { headers: { "User-Agent": "WhereWeMeet/0.1" } },
    );
    if (!resp.ok) return null;
    const data = await resp.json();
    const a = data.address ?? {};
    const parts: string[] = [];
    if (a.road) parts.push(a.road);
    if (a.neighbourhood) parts.push(a.neighbourhood);
    else if (a.suburb) parts.push(a.suburb);
    if (a.city || a.town || a.village)
      parts.push(a.city || a.town || a.village);
    return parts.length > 0
      ? parts.join(", ")
      : data.display_name?.split(",").slice(0, 3).join(",") ?? null;
  } catch {
    return null;
  }
}
